import UserService from "@api/userService";
import { injectChild } from "@components/constructor";
import { usePromisedDispatch } from "@hooks/helperHooks";
import { FileUploader } from "@services/index";
import { panelActions } from "@store/panelStore";
import { userActions } from "@store/user";
import {ChangeEvent, FC, PropsWithChildren, RefObject, useCallback, useEffect, useMemo, useRef, useState} from "react";
import { ReactCropperElement } from "react-cropper";
import { toastr } from "@/utils/toastHelper";
import { AvatarProvider, IAvatarContext } from "./context";
import {useLocalizedBlock} from "@services/hooks";

interface AvatarUploaderChildProps {
    on_avatar_selected: (event: ChangeEvent<HTMLInputElement>) => any;
    upload_avatar: () => Promise<any>;
    avatar_selected: boolean;
    avatar_uploading: boolean;
    avatar_upload_progress: number;
    avatar_input_ref: RefObject<HTMLInputElement | null>;
    avatar_input_click: () => void;
}

interface AvatarUploaderProps {
    modalToggle?: () => void;
    open_modal_id?: string;
}

const AvatarUploader: FC<PropsWithChildren<AvatarUploaderProps>> = ({children, modalToggle, open_modal_id}) => {

    const cropperRef = useRef<ReactCropperElement>(null);
    const dispatch = usePromisedDispatch();
    const messages = useLocalizedBlock("forms.settings.avatar.toast");

    const [imageUrl, setUrl] = useState<string | null>(null);
    const [progress, setProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const avatarInputRef = useRef<HTMLInputElement>(null);
    const clickAvatarInput = useCallback(() => {
        avatarInputRef.current?.click();
    }, []);

    const state = useMemo<IAvatarContext>(() => ({
        cropper_ref: cropperRef,
        imageUrl
    }), [imageUrl]);

    useEffect(() => {
        setUrl(url => {
            if (url) {
                URL.revokeObjectURL(url);
            }
            return null;
        });
    }, []);

    const on_avatar_selected = useCallback(({ currentTarget: { files } }: ChangeEvent<HTMLInputElement>) => {
        const file = (files && files.length > 0) ? files[0] : null;
        if (file) {
            setUrl((lastUrl) => {
                if (lastUrl) {
                    URL.revokeObjectURL(lastUrl);
                }
                return URL.createObjectURL(file);
            });

            if(open_modal_id) {
                dispatch(panelActions.setModalState({
                    id: open_modal_id,
                    state: true
                }));
            }
            if(modalToggle) {
                modalToggle();
            }
        }
        else {
            setUrl(null)
        }
    }, [dispatch, modalToggle, open_modal_id]);

    const upload_avatar = useCallback(async () => {

        const cropper = cropperRef.current?.cropper;
        if(!cropper) {
            return;
        }
        
        setUploading(true);
        cropper.getCroppedCanvas().toBlob(async blob => {

            if(!blob) {
                return;
            }

            const api = new UserService();

            const result = await api.prepareAvatarUpload();
            if(!result.success) {
                setUploading(false);
                return;
            }
    
            const uploader = new FileUploader(result.data);
            uploader.onProgress = (event: ProgressEvent<EventTarget>) => setProgress(Math.round(event.loaded / event.total * 100));
    
            try {
                await uploader.uploadBlob(blob, 'avatar.png');
                toastr.success(messages['title'], messages['success']);
                await dispatch(userActions.data.loadUserData());
    
                if(modalToggle) {
                    modalToggle();
                }

                setUrl(imageUrl => {
                    if (imageUrl) {
                        URL.revokeObjectURL(imageUrl);
                    }
                    return null;
                });
            }
            catch(e) {
                toastr.error(messages['title'], messages['error']);
            }
            finally {
                setProgress(0);
                setUploading(false);
            }
        });
    }, [dispatch, modalToggle]);

    const data = useMemo<AvatarUploaderChildProps>(() => ({
        on_avatar_selected,
        upload_avatar,
        avatar_selected: Boolean(imageUrl),
        avatar_upload_progress: progress,
        avatar_uploading: uploading,
        avatar_input_ref: avatarInputRef,
        avatar_input_click: clickAvatarInput
    }), [clickAvatarInput, imageUrl, on_avatar_selected, progress, upload_avatar, uploading]);

    return (
        <AvatarProvider value={state}>
            {injectChild(data, children)}
        </AvatarProvider>
    )
}

export default AvatarUploader;